import React, { memo, useEffect, useState } from 'react';

import MilestoneTracker from 'components/logistics/fulfillmentLifecycle/components/MilestoneTracker';
import { useRouteBreakdown } from 'components/logistics/fulfillmentLifecycle/hooks/useRouteBreakdown';
import PalletsTab from 'components/logistics/fulfillmentLifecycle/modals/tabs/PalletsTab';
import StatusBreakdownTab from 'components/logistics/fulfillmentLifecycle/modals/tabs/StatusBreakdownTab';
import { formatPullTime, getRouteName } from 'components/logistics/fulfillmentLifecycle/utils/formatters';
import { Badge } from 'shadcn/components/ui/Badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shadcn/components/ui/Tabs';

/** Duration (ms) to keep content visible during the Dialog close animation. */
const EXIT_ANIMATION_MS = 200;

/**
 * RouteDetailModal
 *
 * Modal dialog showing detailed route information with tab navigation
 * for Status Breakdown, Pallets, and Manifests views.
 *
 * Fetches route breakdown data (pallets + status breakdown) at the modal level
 * so that all tabs can share the same API response without duplicate requests.
 *
 * The Dialog stays mounted so that shadcn/Radix exit animations play correctly.
 * The `open` state is derived from the presence of a `route` object.
 *
 * @param {Object} props
 * @param {number|null} props.facilityId - Facility ID for breakdown API calls
 * @param {Object|null} props.route - Route object from the /status API response
 * @param {Function} props.onOpenChange - Callback when open state changes (receives boolean)
 * @param {string} props.selectedDate - Operating date in YYYY-MM-DD format
 */
function RouteDetailModal({ facilityId, onOpenChange, route, selectedDate }) {
    const open = route !== null;
    const [displayRoute, setDisplayRoute] = useState(route);

    useEffect(() => {
        if (route) {
            setDisplayRoute(route);
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setDisplayRoute(null);
        }, EXIT_ANIMATION_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [route]);

    const routeForContent = route ?? displayRoute;
    const routeName = routeForContent ? getRouteName(routeForContent) : '';
    const formattedPullTime = routeForContent
        ? (formatPullTime(routeForContent.pullTime) ?? routeForContent.pullTime ?? '—')
        : '';

    // Fetch breakdown data (pallets + status breakdown) at the modal level.
    // Uses the live `route` (not displayRoute) so the request aborts on close.
    const {
        breakdownData,
        error: breakdownError,
        loading: breakdownLoading,
    } = useRouteBreakdown({
        route,
        facilityId,
        selectedDate,
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
                {routeForContent ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <span>
                                    {routeName} - {routeForContent.hub}
                                </span>
                                <Badge variant="outline" className="text-xs font-normal">
                                    {routeForContent.carrier}
                                </Badge>
                            </DialogTitle>
                            <DialogDescription>
                                Pull Time: {formattedPullTime} &middot; Pack Date: {selectedDate}
                            </DialogDescription>
                        </DialogHeader>

                        {/* Milestone Progress Tracker */}
                        <MilestoneTracker route={routeForContent} />

                        {/* Tab Navigation */}
                        <Tabs defaultValue="statusBreakdown">
                            <TabsList>
                                <TabsTrigger value="statusBreakdown">Status Breakdown</TabsTrigger>
                                <TabsTrigger value="pallets">Pallets</TabsTrigger>
                                <TabsTrigger value="manifests">Manifests</TabsTrigger>
                            </TabsList>

                            <TabsContent value="statusBreakdown">
                                <StatusBreakdownTab
                                    breakdowns={breakdownData?.breakdowns}
                                    loading={breakdownLoading}
                                    error={breakdownError}
                                />
                            </TabsContent>

                            <TabsContent value="pallets">
                                <PalletsTab
                                    pallets={breakdownData?.pallets}
                                    loading={breakdownLoading}
                                    error={breakdownError}
                                />
                            </TabsContent>

                            <TabsContent value="manifests">
                                <div className="py-8 text-center" data-testid="tab-manifests">
                                    <p className="text-sm text-muted-foreground">
                                        Manifests view coming soon (BIZ-8175)
                                    </p>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

export default memo(RouteDetailModal);
